/* eslint-disable react/no-array-index-key */
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { Waypoint } from 'react-waypoint';
import times from 'lodash/times';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Toggle } from '../CommonComponents/OPL/Toggle/Toggle';
import { Feature } from '../CommonSelectors/CommonArea.types';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';
import { useFeatureFlag } from '../_hooks/useFeatureFlag';
import { UserActivityItemLoading } from './components/UserActivityItem/UserActivityItem.loading';
import { UserActivityItem } from './components/UserActivityItem/UserActivityItem';
import { AddUserActivity } from './components/AddUserActivity/AddUserActivity';
import { SortFilter } from './components/SortFilter/SortFilter';
import { EmptyState } from '../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { UserActivityLoading } from './UserActivity.loading';
import { draftColor, mergeActivityPages } from './UserActivity.meta';
import { useUserActivity } from './useUserActivity';

interface Props {
  clientId: string;
}

export const UserActivity = ({ clientId }: Props) => {
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const showAutomaticAccountUpdatesToggle = useFeatureFlag(
    Feature.EnableHideAutomaticAccountUpdatesToggle,
  );
  const sortFilterEnabled = useFeatureFlag(Feature.EnableUserActivitySortAndFilter);
  const [hideAutomatic, setHideAutomatic] = useState(showAutomaticAccountUpdatesToggle);

  const {
    data: {
      sortOrder,
      selectedActivityFilterIds,
      selectedUserFilterIds,
      filterOptions,
      hasFilterOptions,
      activityLog,
      isLoading,
      isError,
      isFetchingNextPage,
      hasNextPage,
      failureCount,
      isDataFiltered,
      showFilterPopover,
    },
    actions: { applySortFilterChange, refetch, fetchNextPage, setShowFilterPopover, resetFilters },
  } = useUserActivity({ clientId, hideAutomatic, sortFilterEnabled });

  useDocumentTitle('User Activity');

  const activityPages = activityLog?.pages ?? [];
  const mergedItems = useMemo(() => mergeActivityPages(activityPages, sortOrder), [
    activityPages,
    sortOrder,
  ]);
  const hasAnyActivities = mergedItems.length > 0;

  if (isLoading) {
    return (
      <UserActivityLoading
        showAutomaticAccountUpdatesToggle={showAutomaticAccountUpdatesToggle}
        showSortFilter={sortFilterEnabled}
      />
    );
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  return (
    <Box css="margin-top: -16px">
      <Box as="header" position="sticky" top={0} bg="bg" zIndex={2} pb="16" pt="16">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb="16">
          <Text as="h1" fontSize="h5" fontWeight={600} color="neutral.600" m="0">
            User Activity
          </Text>
          <Box display="flex" css={({ theme }) => ({ gap: theme.space['8'] })}>
            <Button leftIcon="fas fa-plus" onClick={() => setShowAddActivityModal(true)}>
              Add Activity
            </Button>
            {sortFilterEnabled && filterOptions != null && hasFilterOptions && (
              <SortFilter
                isOpen={showFilterPopover}
                onOpenChange={setShowFilterPopover}
                filterOptions={filterOptions}
                sortOrder={sortOrder}
                selectedActivityFilterIds={selectedActivityFilterIds}
                selectedUserFilterIds={selectedUserFilterIds}
                onApplyChange={applySortFilterChange}
                isDataFiltered={isDataFiltered}
              />
            )}
          </Box>
        </Box>

        {showAutomaticAccountUpdatesToggle && (
          <Toggle
            checked={hideAutomatic}
            onChange={ev => setHideAutomatic(ev.target.checked)}
            mt="8"
          >
            Hide Automatic Account Updates
          </Toggle>
        )}
      </Box>

      {!hasAnyActivities && (
        <EmptyState
          message={
            isDataFiltered
              ? 'No activity has been recorded for the selected filters yet.'
              : 'No activity has been recorded for this client yet.'
          }
          my="48"
        />
      )}

      {mergedItems.map(({ date, dayActivity }) => (
        <Box key={date} my="24">
          <Text as="h2" fontSize="h6" fontWeight={500} mb="12">
            {moment(date).format('MMMM DD, YYYY')}
          </Text>
          {dayActivity.map(({ initial, activities }, j) => (
            <Box key={j} border="1px solid" borderRadius="medium" borderColor="neutral.300">
              {activities.map((activity, k) => (
                <Box
                  key={k}
                  display="flex"
                  alignItems="center"
                  p="16"
                  borderTop={k === 0 ? 'none' : '1px solid'}
                  borderColor="neutral.300"
                >
                  <UserActivityItem
                    key={activity.eventId}
                    activity={activity}
                    color={draftColor(initial)}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      ))}

      {isFetchingNextPage || failureCount > 0 ? (
        <Box border="1px solid" borderRadius="medium" borderColor="neutral.300">
          {times(3).map(key => (
            <Box
              key={key}
              display="flex"
              alignItems="center"
              p="16"
              borderTop={key === 0 ? 'none' : '1px solid'}
              borderColor="neutral.300"
            >
              <UserActivityItemLoading />
            </Box>
          ))}
        </Box>
      ) : (
        hasNextPage && <Waypoint onEnter={() => (fetchNextPage ? fetchNextPage() : {})} />
      )}

      {hasAnyActivities && !hasNextPage && (
        <p style={{ textAlign: 'center', color: '#6C727F' }}>
          There are no further items available in this user&apos;s activity log.
        </p>
      )}

      {showAddActivityModal && (
        <AddUserActivity
          clientId={clientId}
          onClose={() => setShowAddActivityModal(false)}
          hideAutomatic={hideAutomatic}
          isDataFiltered={isDataFiltered}
          resetFilters={resetFilters}
        />
      )}
    </Box>
  );
};
